import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useGlobalState } from "../../utilities/hooks/use-global-state";
import { CategoryIds } from "../../models/enumerations/category-ids";
import { SlideType } from "../../models/enumerations/slide-types";
import AssessmentSettings from "../../models/interfaces/assessment-settings";
import GlobalStateError from "../../models/interfaces/global-state-error";
import { Question } from "../../models/interfaces/questions/question";
import { CalculateMovementScore } from "../../models/interfaces/scores/calculate-movement-score";
import { MovementScore } from "../../models/interfaces/scores/movement-score";
import { MskScore } from "../../models/interfaces/scores/msk-score";
import { AssessmentUtils } from "../../utilities/assessments/assessment-utils";
import { CorrectiveProgramUtils } from "../../utilities/corrective-program-utils";
import GlobalStateUtil from "../../utilities/global-state-util";
import MskScoreUtil from "../../utilities/msk-score-util";
import CalculateMovementService from "../../utilities/services/calculate/calculate-movement-service";
import MovementScoreService from "../../utilities/services/movement-score-service/movement-score-service";
import MskScoreServiceAssessments from "../../utilities/services/msk-score-service-assessments/msk-score-service-assessments";
import UserMskFocusArticlesUtil from "../../utilities/user-msk-focus-articles-util";
import UserUtilAssessments from "../../utilities/user-util-assessments";
import LoaderAssessments from "../loader-assessments/loader-assessments";
import ScreenSlide from "../screen-slide/screen-slide";
import { useStoreAssessments } from "../../store/useStoreAssessments";
// import TodayService from "../../../utilities/services/today-service/today-service";
import { DateTime } from "luxon";
import ReportService from "../../utilities/services/report-service-assessments/report-service-assessments";
import UserService from "../../utilities/services/user-service";
import { Report } from "../../models/interfaces/report";
import { ReportType } from "../../models/enumerations/report-type";
import UserCorrectiveProgramsService from "../../utilities/services/corrective-programs-service/user-corrective-programs-service";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import BaseCardLayoutActions from "../base-card-layout/base-card-layout-actions";
import UserActivity from "../../models/interfaces/user-activity";
import UserActivityService from "../../utilities/services/user-activity-service";
import { ActivityType } from "../../models/enumerations/activity-type";
import { useIntercom } from "react-use-intercom";
import WellnessProgramService from "../../utilities/services/wellness-program-service";
import ArticleUtils from "../../utilities/article-utils";
import { WellnessProgram } from "../../models/interfaces/wellness-program";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import { GroupIds } from "../../models/enumerations/group-ids";
import { useTranslation } from 'react-i18next';
import { UserInfo } from "../../models/interfaces/scores/user-info";
import UserMskFocusArticlesService from "../../utilities/services/article-service/user-msk-articles-service";
import { useNavigationDirection } from "../navigation-direction-context/navigation-direction-content";
import { enqueueSnackbar } from "notistack";
import i18n from "../../i18n";
import { scheduleAiMskScoreSummary } from "../../utilities/ai-response-utils";
import { createLogger } from "../../utilities/logger";

const log = createLogger("MovementScreen");
const COMPONENT_CLASS = "c-movement-screen";

export interface MovementScreenProps {
    screenSettings: AssessmentSettings;
    setScreenSettings: React.Dispatch<AssessmentSettings>;
    settingsOnBack?: Function;
    onSubmit?: () => Promise<void>;
}

const MovementScreen: React.FC<MovementScreenProps> = (props) => {
    const { trackEvent } = useIntercom();
    const { screenSettings, setScreenSettings, onSubmit: onSubmitCallback } = props;
    const { setGlobalError, setGlobalErrors, setHasMskScore } = useGlobalState();
    const { state, setState } = useAuthState();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [userInfo, setUserInfo] = useState<UserInfo>();
    const [movementScore, setMovementScore] = useState<
        MovementScore | undefined | null
    >(undefined);
    const [errors, setErrors] = useState<string[]>([]);
    //const { currentIdentity } = useStoreAssessments((state) => ({
    //     currentIdentity: state.currentIdentity,
    //     setIdentity: state.setIdentity,
    // }));
    const setLastActivityCompletedDate = useStoreAssessments(
        (state) => state.setLastActivityCompleted
    );
    const { t } = useTranslation();
    const { setDirection } = useNavigationDirection();



    useEffect(() => {
        if (!state.user || !state.user.id) {
            new Error("Please log in.");
            return;
        }

        UserUtilAssessments.getUserInfo(state.user.id).then((userInfo) => {
            if (!userInfo) {
                // they don't have bioSex or birthdate. Take them to setup screen
                history.replace("/setup");
            }
            setUserInfo(userInfo);
        });

        AssessmentUtils.fetchQuestions(
            screenSettings,
            CategoryIds.MOVEMENT
        ).then((settings) => {
            setScreenSettings(settings);
            setIsLoading(false);
        }).catch((e) => {
            log.error("Failed to load movement questions", {
                exception: e,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNextPage = async () => {
        setErrors([]);
        setDirection("forward");
        setScreenSettings(await AssessmentUtils.moveToNextPage(screenSettings, undefined));
    };

    const recordAnswer = async (selectedAnswer: string, question: Question, forceAutoSubmit?: boolean) => {
        let settings = AssessmentUtils.recordAnswer(
            screenSettings,
            question,
            selectedAnswer
        );
        if (errors.length !== 0) {
            setErrors([]);
        }

        if (
            slide?.slideType === SlideType.Question &&
            slide.questions != null
        ) {
            let autoSubmit = false;
            slide.questions.forEach((question) => {
                if (AssessmentUtils.autoSubmitQuestion(question)) {
                    autoSubmit = true;
                }
            });
            setDirection("forward")
            if (autoSubmit || forceAutoSubmit) {
                settings = await AssessmentUtils.moveToNextPage(settings, undefined);
            }
        }
        setScreenSettings(settings);
    };

    const submit = async (attemptIndex: number = 0) => {
        setIsLoading(true);
        const queryParams = new URLSearchParams(window.location.search);
        if (!state.user || !state.user.id) {
            return;
        }
        if (!userInfo) {
            // they don't have bioSex or birthdate. Take them to setup screen
            history.replace("/setup");
            return;
        }
        const userId = state.user.id;
        const mskScore = await MskScoreUtil.getLatest(userId);
        const calcScore: CalculateMovementScore = {
            userInfo: userInfo,
            movementResponses: AssessmentUtils.getAllResponses(screenSettings),
            existingMskScore: mskScore,
        };

        let newMskScore: MskScore | null = null;
        try {
            newMskScore = await CalculateMovementService.score(calcScore);
        } catch (err) {
            if (attemptIndex === 0) {
                setGlobalError("Movement: Calculate", err);
            }
            const retryMs: number[] = [500, 1000, 1500, 3000, 5000];
            if (attemptIndex < retryMs.length) {
                setTimeout(() => {
                    return submit(attemptIndex + 1);
                }, retryMs[attemptIndex]);
                return;
            } else {
                log.error("Failed to calculate movement score after retries", {
                    exception: err,
                    attempts: attemptIndex + 1,
                });
            }
        }
        if (!mskScore?.percentage && newMskScore?.percentage) {
            trackEvent('first-time-assessments-completed');
        }

        if (newMskScore == null) {
            log.error("Movement score calculation returned no result");
            setErrors(["Movement MSK Score could not be calculated"]);
            setIsLoading(false);
            return;
        }
        log.info("Movement score calculated", {
            mskScoreId: newMskScore.id,
            percentage: newMskScore.percentage,
            movementPercentage: newMskScore.movementScore?.percentage,
        });
        setMovementScore(newMskScore.movementScore);
        setErrors(newMskScore.errors);
        if (newMskScore.percentage == null) {
            const userActivity: UserActivity = {
                date: DateTime.now().toISODate(),
                type: ActivityType.WebBaselineMovementAssessment,
                userId: state.user?.id ?? ""
            }

            await UserActivityService.add(userActivity, state.user).catch((e) => {
                log.error("Failed to record baseline movement assessment activity", {
                    exception: e,
                    activityType: ActivityType.WebBaselineMovementAssessment,
                });
            });
            setLastActivityCompletedDate(
                new Date(),
                ActivityType.WebBaselineMovementAssessment
            );
        }
        else {
            const userActivity: UserActivity = {
                date: DateTime.now().toISODate(),
                type: ActivityType.WebMovementAssessment,
                userId: state.user?.id ?? ""
            }

            await UserActivityService.add(userActivity, state.user).catch((e) => {
                log.error("Failed to record movement assessment activity", {
                    exception: e,
                    activityType: ActivityType.WebMovementAssessment,
                });
            });
            setLastActivityCompletedDate(
                new Date(),
                ActivityType.WebMovementAssessment
            );
        }

        // Remove Retired PainSeverity
        MskScoreUtil.retireMovementPainSeverity(newMskScore);
        await recordResults(newMskScore);

        await UserCorrectiveProgramsService.deleteByUser(userId).catch((e) => {
            log.error("Failed to delete prior corrective programs after movement assessment", {
                exception: e,
            });
        });
        // If both assessments aren't completed
        if (newMskScore?.percentage == null) {
            history.push("/wellness-assessment");
        }
        // If the user is in the Retake Assessments Flow
        else if (queryParams.get("retake") === "true") {
            history.push("/lifestyle?retake=true");
        }
        // Else, this is the user's 2nd and last assessment and they are done.
        else {
            // We are creating a CE instead of checking if one exists because after a retest, there must be a new CE routine
            // Logic in today is to create CE and ECE
            // checking mskScore to ensure correctives and articles should be created.
            if (state.user && state.user.id && state.user.symmioAccess === SymmioAccessType.AppLicense && newMskScore.movementScore?.percentage != null && newMskScore.focusAreas != null &&
                newMskScore.focusAreas.length >= 1) {
                let wellnessProgram: WellnessProgram = {
                    createdAtLocal: DateTime.now().toISO(),
                    lengthInDays: 0,
                    focusAreas: undefined,
                    movementFocusAreas: undefined,
                    currentDay: 1,
                    correctiveExerciseId: undefined,
                    focusArticleId: undefined,
                    isCurrent: true,
                    userId: state.user.id,
                    currentProgress: [],
                    numOfAdherenceAccountedFor: 0,
                }
                try {
                // Before we create correctives/articles, we must set any isCurrent = true to false
                const currentCorrectiveExercises = await UserCorrectiveProgramsService.getCurrentByUserId(state.user.id)
                const currentExtraCorrectiveExercises = await UserCorrectiveProgramsService.getCurrentExtraByUserId(state.user.id)
                const currentArticles = await UserMskFocusArticlesService.getAllCurrentByUserId(state.user.id)
                if (currentCorrectiveExercises && currentCorrectiveExercises.length > 0) {
                    for (const correctiveExercise of currentCorrectiveExercises) {
                        correctiveExercise.isCurrent = false;
                        await UserCorrectiveProgramsService.update(correctiveExercise);
                    }
                }
                if (currentExtraCorrectiveExercises && currentExtraCorrectiveExercises.length > 0) {
                    for (const correctiveExercise of currentExtraCorrectiveExercises) {
                        correctiveExercise.isCurrent = false;
                        await UserCorrectiveProgramsService.update(correctiveExercise);
                    }
                }
                if (currentArticles && currentArticles.length > 0) {
                    for (const article of currentArticles) {
                        article.isCurrent = false;
                        await UserMskFocusArticlesService.update(article);
                    }
                }                // Create correctiveExercise
                // For some reason old team made it so if there is no extra corrective, still create a corrective of 100 days and no routine
                const ce = await CorrectiveProgramUtils._createUserCorrectiveProgram(state.user.id);
                const articles = await ArticleUtils.createUserMskArticles(state.user.id, newMskScore)

                if (ce && ce.hasCorrectiveProgram) {
                    wellnessProgram.correctiveExerciseId = ce.userCorrectiveProgram.id;
                    wellnessProgram.lengthInDays = ce.userCorrectiveProgram.routineDays;
                }
                // Create article
                if (articles && articles.id) {
                    wellnessProgram.focusArticleId = articles.id;
                    let longestArticleLength = 0
                    for (let i = 0; i < articles.focusStatuses.length; i++) {
                        let currentCheckArticleLength = 0
                        if (articles.focusStatuses.length === 1) {
                            if (articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH && articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH_BREATHING) {
                                currentCheckArticleLength = (i + 1) + (2 * (articles.focusStatuses[i].numArticles - 1))
                            }
                        }
                        else {
                            if (articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH && articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH_BREATHING) {
                                currentCheckArticleLength = (i + 1) + (articles.focusStatuses.length * (articles.focusStatuses[i].numArticles - 1))
                            }
                        }
                        if (currentCheckArticleLength > longestArticleLength) {
                            longestArticleLength = currentCheckArticleLength;
                        }
                    }
                    if (longestArticleLength > wellnessProgram.lengthInDays) {
                        wellnessProgram.lengthInDays = longestArticleLength
                    }
                }
                if (newMskScore.focusAreas) {
                    wellnessProgram.focusAreas = newMskScore.focusAreas;
                }
                if (newMskScore.movementScore && newMskScore.movementScore.focusArea) {
                    wellnessProgram.movementFocusAreas = newMskScore.movementScore.focusArea
                }

                // Create wellness Program
                const savedWellnessProgram = await WellnessProgramService.add(wellnessProgram);
                log.info("Wellness program created after movement assessment", {
                    wellnessProgramId: savedWellnessProgram.id,
                    lengthInDays: savedWellnessProgram.lengthInDays,
                    hasCorrectiveProgram: !!ce?.hasCorrectiveProgram,
                    hasArticles: !!articles?.id,
                });
                state.user.wellnessProgramId = savedWellnessProgram.id
                if (state.user.administeredRetake) {
                    state.user.administeredRetake = false
                }
                const stateUser = state.user
                // updating FB user
                await UserService.updateFields(stateUser, {
                    wellnessProgramId: savedWellnessProgram.id,
                    administeredRetake: state.user.administeredRetake
                });
                // update state
                // Set the state to prepare everything for logging in.
                setState((state) => ({
                    ...state, ...{ user: stateUser }
                }));
                } catch (e) {
                    log.error("Failed to create wellness program after movement assessment", {
                        exception: e,
                    });
                }
            }

            // create new aiResponse object
            await scheduleAiMskScoreSummary(newMskScore, state.user?.organizationId).catch((e) => {
                log.error("Failed to schedule AI MSK score summary", {
                    exception: e,
                });
            });

            handleGenerateReport(true, newMskScore);
            history.push("/wellness-assessment");
        }
    };

    /**
     * Record Scores
     * @returns True if succeeeded, false if there was an error
     */
    const recordResults = async (newMskScore: MskScore): Promise<boolean> => {
        if (
            !state.user || state.user.id == null ||
            newMskScore.movementScore == null
        ) {
            return true;
        }


        const userId = state.user.id;
        const newErrors: GlobalStateError[] = [];

        try {
            await MskScoreServiceAssessments.addOrUpdateRecentById(
                newMskScore,
                userId
            ).then(() => {
                if (userId) {
                    MskScoreUtil.getLatest(userId).then((response) => {
                        if (response?.lifestyleScore?.percentage != null &&
                            response.movementScore?.percentage != null) {
                            setHasMskScore(true);
                        }
                    });
                }
            });
        } catch (err) {
            log.error("Failed to save MSK score after movement assessment", {
                exception: err,
                mskScoreId: newMskScore.id,
            });
            newErrors.push(
                GlobalStateUtil.createError(t('assessments.err_mskNotSaved'), err)
            );
        }
        try {
            await MovementScoreService.addOrUpdateRecentById(
                newMskScore.movementScore!,
                state.user.id
            );
        } catch (err) {
            log.error("Failed to save movement score after assessment", {
                exception: err,
                mskScoreId: newMskScore.id,
            });
            newErrors.push(
                GlobalStateUtil.createError(t('assessments.err_scoreNotSaved'), err)
            );
        }
        try {
            await CorrectiveProgramUtils.handleNewMovementScoreById(
                newMskScore.movementScore,
                state.user.id
            );
        } catch (err) {
            log.error("Failed to handle new movement score", {
                exception: err,
                mskScoreId: newMskScore.id,
            });
            newErrors.push(
                GlobalStateUtil.createError(
                    t('assessments.err_scoreNotHandled'),
                    err
                )
            );
        }
        try {
            await UserUtilAssessments.handleNewMskScore(userId, {
                mskScore: newMskScore,
            });
        } catch (err) {
            log.error("Failed to handle new MSK score after assessment", {
                exception: err,
                mskScoreId: newMskScore.id,
            });
            newErrors.push(
                GlobalStateUtil.createError(
                    t('assessments.err_retakeNotUpdated'),
                    err
                )
            );
        }
        try {
            await UserMskFocusArticlesUtil.handleFocusChanges(
                userId,
                newMskScore
            );
        } catch (err) {
            log.error("Failed to apply focus changes after movement assessment", {
                exception: err,
                mskScoreId: newMskScore.id,
            });
            newErrors.push(
                GlobalStateUtil.createError(
                    t('assessments.err_focusNotUpdated'),
                    err
                )
            );
        }

        if (newErrors.length >= 1) {
            log.error("Movement results saved with errors", {
                errors: newErrors.map((x) => x.description),
                mskScoreId: newMskScore.id,
            });
            // set errors
            setErrors(newErrors.map((x) => x.description));

            // update global state with errors
            setGlobalErrors(...newErrors);
            return false;
        }
        return true;
    };
    const groups = AssessmentUtils.getAssessmentGroups(screenSettings);
    const currentGroup =
        screenSettings?.slideGroups == null
            ? null
            : screenSettings.slideGroups[screenSettings.currentGroupIndex];
    const slide = AssessmentUtils.getSlide(screenSettings);
    const actionsModifier =
        slide?.slideType === SlideType.Video ? "-video" : "";

    let hasContinueButton =
        AssessmentUtils.hasData(screenSettings) &&
        movementScore == null &&
        (!AssessmentUtils.isLastGroup(screenSettings) ||
            !AssessmentUtils.isLastSlide(screenSettings));

    if (
        hasContinueButton &&
        slide &&
        slide?.slideType === SlideType.Question &&
        slide.questions != null
    ) {
        slide.questions.forEach((question) => {
            if (AssessmentUtils.autoSubmitQuestion(question)) {
                hasContinueButton = false;
            }
            if (AssessmentUtils.multiQuestion(question)) {
                hasContinueButton = false
            }
        });
    }

    let continueButtonText = t('buttons.btn_continue');
    if (hasContinueButton && slide != null) {
        switch (slide.slideType) {
            case SlideType.AssessmentIntro:
            case SlideType.Tests:
                continueButtonText = t('buttons.btn_letsDoIt');
                break;
            case SlideType.Tips:
                continueButtonText = t('buttons.btn_gotIt');
                break;
            case SlideType.GroupIntro:
                continueButtonText = t('buttons.btn_start');
                break;
            case SlideType.Video:
                if (slide.video) {
                    continueButtonText = t('buttons.btn_enterResults');
                }
                break;
        }
    }

    const hasSubmitButton =
        AssessmentUtils.isLastGroup(screenSettings) &&
        AssessmentUtils.isLastSlide(screenSettings);

    const noProgressBarSlideTypes = [
        SlideType.AssessmentIntro,
        SlideType.Complete,
        SlideType.GroupIntro,
        SlideType.Tests,
        SlideType.Tips,
    ];
    const hasProgressBar =
        slide == null ||
        noProgressBarSlideTypes.indexOf(slide.slideType) === -1;
    const questionGroupNames = AssessmentUtils.getAssessmentGroups(
        screenSettings
    ).map((group) => group.groupId!);

    const handleGenerateReport = async (emailResults: boolean, mskScore: MskScore) => {
        if (!state.user || !state.user.id) return;

        const userInFirebase = await UserService.get(state.user.id);

        if (!userInFirebase || !userInFirebase.organizationId || !userInFirebase.id) return;

        const latestOrgId = userInFirebase.organizationId;

        //setIsLoading(true);

        const date = DateTime.now().toISODate();
        const report: Report = {
            dateEnd: date,
            dateStart: date,
            emailResults: emailResults,
            organizationId: latestOrgId,
            status: "scheduled",
            type: ReportType.Wellness,
            userId: userInFirebase.id,
            mskScoreId: mskScore ? mskScore.id : undefined,
            language: i18n.language,
        };

        const scheduledReport = await ReportService.save(
            report,
            userInFirebase
        ).catch((e) => {
            log.error("Failed to schedule movement report", {
                exception: e,
                organizationId: userInFirebase.organizationId,
            });
            return null;
        });

        if (!scheduledReport || !scheduledReport.id) {
            if (scheduledReport == null) {
                log.error("Movement report was not scheduled", {
                    organizationId: userInFirebase.organizationId,
                });
            } else {
                log.error("Movement report scheduled without an id", {
                    organizationId: userInFirebase.organizationId,
                });
            }
            enqueueSnackbar(t('general.error.reportNotSaved'), { variant: "error" });
            return;
        }

        log.info("Movement report scheduled", {
            reportId: scheduledReport.id,
            mskScoreId: report.mskScoreId,
            emailResults: scheduledReport.emailResults,
        });

        if (scheduledReport.emailResults === true) {
            log.info("Report emailed", {
                reportId: scheduledReport.id,
                emailResults: true,
            });
            enqueueSnackbar(t('pages.lifestyleScreen.emailedReport'), { variant: "toast", width: "600px" });
            return;
        }
    };

    // Wrap the submit function to call custom callback if provided
    const handleSubmit = async () => {
        if (onSubmitCallback) {
            await onSubmitCallback();
        } else {
            await submit();
        }
    };

    // ... rest of component keeps same submit() logic

    // ...

    return (
        <div className={`${COMPONENT_CLASS} -movement`}>
            <LoaderAssessments isLoading={isLoading} />
            {
                // if
                !isLoading && currentGroup != null && (
                    <div
                        className={`${COMPONENT_CLASS}__wrapper ${slide?.slideType === SlideType.GroupIntro
                            ? "-intro"
                            : ""
                            }`}>

                        {
                            // if
                            AssessmentUtils.hasData(screenSettings) &&
                            movementScore == null && (
                                <div
                                    className={`${COMPONENT_CLASS}__content ${hasContinueButton ||
                                        hasSubmitButton
                                        ? "has-button"
                                        : ""
                                        }`}>
                                    {
                                        // if
                                        slide != null && (
                                            <ScreenSlide
                                                questionGroupNames={
                                                    questionGroupNames
                                                }
                                                recordedAnswers={AssessmentUtils.getAllResponses(
                                                    screenSettings
                                                )}
                                                recordAnswer={recordAnswer}
                                                page={slide}
                                                errors={errors}
                                                totalProgress={
                                                    groups.length
                                                }
                                                currentProgress={
                                                    groups.indexOf(
                                                        currentGroup
                                                    ) + 1
                                                }
                                                hasProgressBar={hasProgressBar}
                                                screenSettings={screenSettings}
                                                settingsOnBack={props.settingsOnBack}
                                                isActionsWithContinue={hasContinueButton}
                                                skipButton={slide?.questions &&
                                                    slide.questions.length >
                                                    0 &&
                                                    slide.questions[0]
                                                        .optional === true}
                                                onSkip={getNextPage}
                                                onBack={props.settingsOnBack}
                                                onSubmit={getNextPage}
                                                submitButtonText={continueButtonText}
                                                headerText={t('assessments.movement')}
                                            />
                                        )
                                    }

                                    <div
                                        className={`${COMPONENT_CLASS}__actions ${actionsModifier} ion-margin-horizontal`}>
                                        {/* {
                                            // if
                                            hasContinueButton && (
                                                <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={getNextPage} submitButtonText={continueButtonText}></BaseCardLayoutActions>
                                            )
                                        } */}
                                        {
                                            // if
                                            hasSubmitButton && (
                                                <div className={`c-screen-slide__actions`}>
                                                    <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={handleSubmit} submitButtonText={errors.length === 0
                                                        ? t('buttons.btn_submitResults')
                                                        : t('buttons.btn_resubmit')}></BaseCardLayoutActions>
                                                </div>
                                                // <IonButton
                                                //     color="primary"
                                                //     expand="block"
                                                //     onClick={() =>
                                                //         submit()
                                                //     }>
                                                //     {errors.length === 0
                                                //         ? "Submit Results"
                                                //         : "Resubmit"}
                                                // </IonButton>
                                            )
                                        }
                                    </div>
                                </div>
                            )
                        }
                    </div>
                )
            }
        </div>
    );
};

export default MovementScreen;
